import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useState } from "react";
import { locationApi } from "@/api/account-management/locationApi";
import { useAppContext } from "../../AppContext";
import { useLocalizedStrings } from "../../localization/LocalizedStringsProvider";
import { fillHeight } from "../../theme";

// Shows the pairing QR code for the location selected in the top scope selector.
const PairingCode = () => {
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const locationId = app.locationId ?? undefined;

    const [qrUrl, setQrUrl] = useState<string>();
    const [qrError, setQrError] = useState(false);
    const [reload, setReload] = useState(false);

    // Fetch the QR image (an object URL) for the selected location; refetch when it changes or on retry.
    useEffect(() => {
        if (!locationId) {
            setQrUrl(undefined);
            return;
        }
        let cancelled = false;
        setQrError(false);
        setQrUrl(undefined);
        (async () => {
            try {
                const url = await locationApi.getQrCode(locationId);
                if (cancelled) URL.revokeObjectURL(url);
                else setQrUrl(url);
            } catch {
                if (!cancelled) setQrError(true);
            }
        })();
        return () => { cancelled = true; };
    }, [locationId, reload]);

    // Release the previous object URL when it changes or the page unmounts.
    useEffect(() => {
        return () => { if (qrUrl) URL.revokeObjectURL(qrUrl); };
    }, [qrUrl]);

    return (
        <Box sx={{ ...fillHeight, overflow: "auto", p: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>{strings.pairingCode}</Typography>

            {!locationId ? (
                <Typography color="text.secondary">{strings.pairingCodeSelectLocation}</Typography>
            ) : (
                <Box sx={{ width: 300, height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {qrUrl ? (
                        <img src={qrUrl} alt={strings.pairingCode} style={{ width: "100%", height: "100%" }} />
                    ) : qrError ? (
                        <Stack spacing={1} alignItems="center">
                            <Typography variant="body2" color="text.secondary" textAlign="center">{strings.qrCodeLoadError}</Typography>
                            <Button size="small" startIcon={<RefreshIcon />} onClick={() => setReload(prev => !prev)}>{strings.retry}</Button>
                        </Stack>
                    ) : (
                        <CircularProgress />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default PairingCode;
